import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Typography, Button, Box, Grid, Paper } from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import api from '../services/api';

type OfferDetail = {
  id: number;
  slug: string;
  title_en: string;
  title_pl?: string;
  description_md?: string;
  requirements_md?: string;
  benefits_md?: string;
  salary?: string;
  work_place?: string;
};

const CareerDetailPage: React.FC = () => {
  const { slug } = useParams();
  const [offer, setOffer] = React.useState<OfferDetail | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await api.get(`/recruit/public/offers/${slug}`);
        if (!mounted) return;
        setOffer(resp.data?.data || null);
      } catch (e) {
        setOffer(null);
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  if (loading) return (
    <Container maxWidth="lg" sx={{ py: 6, bgcolor: 'rgba(250, 250, 250, 1)' }}><Typography>Ładowanie…</Typography></Container>
  );
  if (!offer) return (
    <Container maxWidth="lg" sx={{ py: 6, bgcolor: 'rgba(250, 250, 250, 1)' }}><Typography>Nie znaleziono oferty.</Typography></Container>
  );

  // Wynagrodzenie: pierwsza linia do "PLN", druga linia na sztywno "brutto miesięcznie"
  const salaryText = offer.salary || '';
  const salaryMain = (salaryText.match(/^(.*?PLN)/i)?.[1] || salaryText).trim();

  return (
    <Container maxWidth="lg" sx={{ py: 6, bgcolor: '#FAFAFA' }}>
      {/* Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          component="span"
          sx={{
            fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
            fontWeight: 600,
            fontSize: '21.94px',
            lineHeight: '26px',
            letterSpacing: '0.12em',
            color: 'rgba(197, 48, 48, 1)',
            width: '140px',
            height: '26px',
            display: 'inline-block'
          }}
        >
          UNITRESCO
        </Typography>
      </Box>

      {/* Tytuł */}
      <Typography
        component="h1"
        sx={{
          fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
          fontWeight: 600,
          fontSize: '48px',
          lineHeight: '100%',
          letterSpacing: 0,
          color: 'rgba(2, 2, 66, 1)',
          mt: '60px',
          mb: '16px'
        }}
      >
        {offer.title_pl || offer.title_en}
      </Typography>

      {/* Lokalizacja */}
      {offer.work_place && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <PlaceOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">{offer.work_place}</Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8.5 as any} lg={8}>
          {/* Opis stanowiska */}
          {offer.description_md && (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '4px', borderColor: 'rgba(2, 2, 66, 0.14)' }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                  fontWeight: 500,
                  fontSize: '18px',
                  lineHeight: '100%',
                  letterSpacing: 0,
                  color: 'rgba(2, 2, 66, 1)',
                  mb: 1.25
                }}
              >
                Opis stanowiska
              </Typography>
              <Box sx={{
                fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '150%',
                letterSpacing: 0,
                color: 'rgba(89, 89, 102, 1)',
                '& ul': { listStyle: 'none', paddingLeft: '28px' },
                '& ul li': { position: 'relative', marginBottom: '8px' },
                '& ul li::before': {
                  content: '""',
                  position: 'absolute',
                  left: '-24px',
                  top: '2px',
                  width: '18px',
                  height: '18px',
                  backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'18\' height=\'18\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'#DE9364\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'><circle cx=\'12\' cy=\'12\' r=\'9\'/><path d=\'M8 12l2.5 2.5L16 9\'/></svg>')}" )`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '18px 18px'
                }
              }}>
                <div dangerouslySetInnerHTML={{ __html: offer.description_md }} />
              </Box>
            </Paper>
          )}

          {/* Wymagania */}
          {offer.requirements_md && (
            <Paper variant="outlined" sx={{ p: 2.5, mt: 2, borderRadius: '4px', borderColor: 'rgba(2, 2, 66, 0.14)' }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                  fontWeight: 500,
                  fontSize: '18px',
                  lineHeight: '100%',
                  letterSpacing: 0,
                  color: 'rgba(2, 2, 66, 1)',
                  mb: 1.25
                }}
              >
                Wymagania
              </Typography>
              <Box sx={{
                fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '150%',
                letterSpacing: 0,
                color: 'rgba(89, 89, 102, 1)',
                '& ul': { listStyle: 'none', paddingLeft: '28px' },
                '& ul li': { position: 'relative', marginBottom: '8px' },
                '& ul li::before': {
                  content: '""',
                  position: 'absolute',
                  left: '-24px',
                  top: '2px',
                  width: '18px',
                  height: '18px',
                  backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'18\' height=\'18\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'#DE9364\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'><circle cx=\'12\' cy=\'12\' r=\'9\'/><path d=\'M8 12l2.5 2.5L16 9\'/></svg>')}" )`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '18px 18px'
                }
              }}>
                <div dangerouslySetInnerHTML={{ __html: offer.requirements_md }} />
              </Box>
            </Paper>
          )}

          {/* Oferujemy */}
          {offer.benefits_md && (
            <Paper variant="outlined" sx={{ p: 2.5, mt: 2, borderRadius: '4px', borderColor: 'rgba(2, 2, 66, 0.14)' }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                  fontWeight: 500,
                  fontSize: '18px',
                  lineHeight: '100%',
                  letterSpacing: 0,
                  color: 'rgba(2, 2, 66, 1)',
                  mb: 1.25
                }}
              >
                Oferujemy
              </Typography>
              <Box sx={{
                fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '150%',
                letterSpacing: 0,
                color: 'rgba(89, 89, 102, 1)',
                '& ul': { listStyle: 'none', paddingLeft: '28px' },
                '& ul li': { position: 'relative', marginBottom: '8px' },
                '& ul li::before': {
                  content: '""',
                  position: 'absolute',
                  left: '-24px',
                  top: '2px',
                  width: '18px',
                  height: '18px',
                  backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'18\' height=\'18\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'#DE9364\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'><circle cx=\'12\' cy=\'12\' r=\'9\'/><path d=\'M8 12l2.5 2.5L16 9\'/></svg>')}" )`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '18px 18px'
                }
              }}>
                <div dangerouslySetInnerHTML={{ __html: offer.benefits_md }} />
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Prawa kolumna: wynagrodzenie + CTA */}
        <Grid item xs={12} md={3.5 as any} lg={4}>
          <Paper
            variant="outlined"
            sx={{
              width: 486,
              height: 236,
              p: '32px 80px',
              borderRadius: '4px',
              borderColor: 'rgba(2, 2, 66, 0.14)',
              position: 'sticky',
              top: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              transition: 'border-color .2s ease, box-shadow .2s ease',
              '&:hover': {
                borderColor: '#020242',
                boxShadow: '0 0 0 1px rgba(2, 2, 66, 0.6)'
              }
            }}
          >
            {offer.salary && (
              <Box>
                <Typography sx={{
                  fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                  fontWeight: 500,
                  fontSize: '24px',
                  lineHeight: '100%',
                  letterSpacing: 0,
                  color: 'rgba(2, 2, 66, 1)'
                }}>
                  {salaryMain}
                </Typography>
                <Typography sx={{
                  fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                  letterSpacing: 0,
                  color: 'rgba(89, 89, 102, 1)'
                }}>
                  brutto miesięcznie
                </Typography>
              </Box>
            )}
            <Button
              fullWidth
              component={Link}
              to={`/careers/${offer.slug}/apply`}
              sx={{
                textTransform: 'none',
                fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '100%',
                borderRadius: '4px',
                padding: '12px 16px',
                color: 'rgba(255, 255, 255, 1)',
                backgroundColor: 'rgba(63, 63, 220, 1)',
                transition: 'background-color .15s ease',
                '&:hover': { backgroundColor: 'rgba(48, 48, 200, 1)' }
              }}
            >
              Aplikuj
            </Button>
            <Box sx={{ mt: 0, textAlign: 'center' }}>
              <Button
                component={Link}
                to="/careers"
                sx={{
                  textTransform: 'none',
                  fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '100%',
                  borderRadius: 0,
                  padding: 0,
                  color: 'rgba(63, 63, 220, 1)',
                  minWidth: 0,
                  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
                }}
              >
                Zobacz inne oferty Unitresco
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CareerDetailPage;


